import { useCallback, useState } from 'react'
import {
  TIMP_CHART_COLORS,
  usePieChartPngExport
} from '@jana-technology/platform-ui/charts'

import { compactNumberFormatter } from 'helpers/formatters'

import {
  defaultPieChartSize,
  defaultPieChartSliceGap,
  PieChartContainer
} from './PieChartContainer'
import { PieInnerTexts } from './PieInnerTexts'
import {
  type RiskByCategoryRangeValueType,
  type RiskBySystemProps
} from './types'
import {
  buildPieChartConfig,
  formatComparisonSeriesName,
  getRiskByCategoryItems,
  getRiskByCategoryPieConfig,
  getSeriesIdSuffix,
  getUpdatedActivePieDataById,
  hasPieChartData,
  wisRiskBySystemColorRange,
  type ActivePieDataById
} from './utils'

type RiskBySystemLegendClickData = Parameters<
  NonNullable<RiskBySystemProps['onLegendItemClick']>
>[0]

const getRiskInnerTextLabel = (
  rangeValueType: RiskByCategoryRangeValueType = 'total'
) => {
  const riskLabel = rangeValueType === 'density' ? 'Risk Density' : 'Total Risk'

  return riskLabel
}

const getRiskInnerTextValue = (
  totalRisk: number | undefined,
  rangeValueType: RiskByCategoryRangeValueType = 'total'
) => {
  if (typeof totalRisk !== 'number') {
    return rangeValueType === 'density' ? 'RU/mile/year' : 'RU/yr'
  }

  const formattedValue = compactNumberFormatter.format(totalRisk)

  return `${formattedValue} ${
    rangeValueType === 'density' ? 'RU/mile/year' : 'RU/yr'
  }`
}

const getRiskTooltipUnitSymbol = (
  rangeValueType: RiskByCategoryRangeValueType = 'total'
) => (rangeValueType === 'density' ? ' RU/mile/year' : ' RU/yr')

const getRiskInnerTextTotal = (
  seriesName: string,
  baselineRiskTotal: number | undefined,
  scenarioRiskTotal: number | undefined
) => (seriesName === 'Baseline' ? baselineRiskTotal : scenarioRiskTotal)

const getAdjustedSystemRisk = (
  totalRisk: number | undefined,
  { activeSliceCount, visiblePie }: RiskBySystemLegendClickData
) => {
  if (totalRisk == null) {
    return undefined
  }

  if (activeSliceCount === visiblePie.data.length) {
    return totalRisk
  }

  const dataTotal = visiblePie.data.reduce(
    (total, datum) => total + datum.value,
    0
  )
  const activeDataTotal = visiblePie.activeData?.reduce(
    (total, datum) => total + datum.value,
    0
  )

  return totalRisk - (dataTotal - (activeDataTotal ?? 0))
}

const isTotalOrBaselineName = (name?: string) => {
  if (name == null) {
    return true
  }

  const normalizedName = name.toLowerCase()

  return (
    normalizedName.startsWith('total') || normalizedName.startsWith('baseline')
  )
}

export const RiskBySystem = ({
  categoryOrder,
  data,
  isCleared,
  onLegendItemClick,
  rangeValueType,
  title,
  unitSymbol,
  ...props
}: RiskBySystemProps) => {
  const [adjustedTotalBaselineRisk, setAdjustedTotalBaselineRisk] = useState(
    data?.totalRiskBaseline
  )
  const [adjustedTotalScenarioRisk, setAdjustedTotalScenarioRisk] = useState(
    data?.totalRiskScenario
  )
  const [activeDataByPieId, setActiveDataByPieId] = useState<ActivePieDataById>(
    {}
  )

  const {
    items: pieItems,
    hasMultipleSeries,
    series,
    tooltipVariant
  } = getRiskByCategoryPieConfig({
    categoryOrder,
    chartItems: getRiskByCategoryItems(data),
    rangeValueType
  })

  const riskTooltipUnitSymbol =
    unitSymbol ?? getRiskTooltipUnitSymbol(rangeValueType)

  const handleLegendItemClick = useCallback(
    (legendClickData: RiskBySystemLegendClickData) => {
      setActiveDataByPieId((currentActiveDataByPieId) =>
        getUpdatedActivePieDataById(currentActiveDataByPieId, legendClickData)
      )

      if (isTotalOrBaselineName(legendClickData.visiblePie.name)) {
        const adjustedBaselineRisk = getAdjustedSystemRisk(
          data?.totalRiskBaseline,
          legendClickData
        )
        setAdjustedTotalBaselineRisk(adjustedBaselineRisk)
      } else {
        const adjustedScenarioRisk = getAdjustedSystemRisk(
          data?.totalRiskScenario,
          legendClickData
        )
        setAdjustedTotalScenarioRisk(adjustedScenarioRisk)
      }

      onLegendItemClick?.(legendClickData)
    },
    [data?.totalRiskBaseline, data?.totalRiskScenario, onLegendItemClick]
  )

  const pies = series.map((seriesItem, index) => {
    const pieId = `risk-by-system-${getSeriesIdSuffix(index)}`

    return buildPieChartConfig({
      id: pieId,
      items: pieItems,
      name: hasMultipleSeries ? seriesItem.name : undefined,
      seriesKey: seriesItem.name,
      values: seriesItem.values,
      activeData: activeDataByPieId[pieId],
      isRiskSignificance: false,
      onLegendItemClick: handleLegendItemClick,
      tooltipVariant,
      unitSymbol: riskTooltipUnitSymbol
    })
  })

  // blend the main color range with faded WIS colors and sequence them
  const colorRange = hasMultipleSeries
    ? series.flatMap((seriesItem) => {
        const seriesColors =
          seriesItem.name === 'Baseline'
            ? TIMP_CHART_COLORS
            : wisRiskBySystemColorRange

        // recycle colors if values exceeds the color range (10)
        return seriesItem.values.flatMap((value, index) =>
          value ? [seriesColors[index % seriesColors.length]] : []
        )
      })
    : undefined

  const innerTexts = (
    <PieInnerTexts
      formatSeriesName={formatComparisonSeriesName}
      getValue={(seriesName) =>
        getRiskInnerTextValue(
          getRiskInnerTextTotal(
            seriesName,
            adjustedTotalBaselineRisk,
            adjustedTotalScenarioRisk
          ),
          rangeValueType
        )
      }
      isVisible={!isCleared}
      label={getRiskInnerTextLabel(rangeValueType)}
      series={series}
    />
  )

  const parentRef = usePieChartPngExport({
    colorRange,
    pies,
    restrictVerticalWidth: false,
    size: defaultPieChartSize,
    sliceGap: defaultPieChartSliceGap,
    title,
    ...props,
    children: hasPieChartData(pies) ? innerTexts : null
  })

  return (
    <div ref={parentRef} className='h-full w-full min-w-0'>
      <PieChartContainer colorRange={colorRange} pies={pies} {...props}>
        {innerTexts}
      </PieChartContainer>
    </div>
  )
}
